import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PosterCard from "@/components/base/PosterCard";
import { getAllSummaries } from "@/utils/media";
import { useSeo } from "@/hooks/useSeo";
import type { MediaType } from "@/types/media";

type TypeFilter = "all" | MediaType;

const typeTabs: { key: TypeFilter; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q")?.trim() || "";
  const typeParam = (searchParams.get("type") as TypeFilter) || "all";
  const [input, setInput] = useState(keyword);

  useSeo({
    title: keyword ? `“${keyword}”搜索结果 - 天堂电影网` : "搜索影视 - 天堂电影网",
    description: keyword
      ? `天堂电影网为你找到与“${keyword}”相关的影视作品，支持按电影、电视剧分类查看搜索结果。`
      : "在天堂电影网搜索电影与电视剧，输入关键词即可快速找到想看的内容。",
    keywords: `影视搜索,${keyword},天堂电影网搜索,电影搜索,电视剧搜索`,
  });

  const all = useMemo(() => getAllSummaries(), []);

  const results = useMemo(() => {
    let list = all;
    if (typeParam !== "all") list = list.filter((item) => item.type === typeParam);
    if (!keyword) return list;
    const kw = keyword.toLowerCase();
    return list.filter((item) => {
      const haystack = [item.title, item.director, ...item.actors, ...item.genres, item.country]
        .join(" ")
        .toLowerCase();
      return haystack.includes(kw);
    });
  }, [all, keyword, typeParam]);

  const updateParams = (next: { q?: string; type?: TypeFilter }) => {
    const params = new URLSearchParams(searchParams);
    if (next.q !== undefined) {
      if (next.q) params.set("q", next.q);
      else params.delete("q");
    }
    if (next.type !== undefined) {
      if (next.type === "all") params.delete("type");
      else params.set("type", next.type);
    }
    setSearchParams(params, { replace: true });
  };

  const hot = useMemo(() => [...all].sort((a, b) => b.rating - a.rating).slice(0, 5), [all]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateParams({ q: input.trim() });
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <SiteHeader />
      <main className="flex-1 pt-16 md:pt-20">
        <div className="w-full px-4 md:px-8 pt-10 md:pt-14 pb-8 bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1400px] mx-auto">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">搜索影视</h1>
            <p className="mt-2 text-sm text-foreground-400">支持影片名称、导演、演员、类型与地区关键词检索</p>

            <form onSubmit={handleSubmit} className="mt-6 max-w-2xl" role="search">
              <div className="flex items-center bg-background-50 border border-background-300/60 rounded-lg pl-4 pr-1.5 h-12 focus-within:border-primary-400 transition-colors">
                <i className="ri-search-line text-lg text-foreground-400" aria-hidden="true" />
                <input
                  type="text"
                  name="q"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="输入影片名称、导演或演员"
                  aria-label="搜索关键词"
                  className="flex-1 bg-transparent text-sm md:text-base text-foreground-900 placeholder:text-foreground-400 px-3 outline-none"
                />
                <button
                  type="submit"
                  className="h-9 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  搜索
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <p className="text-sm text-foreground-500">
                {keyword ? (
                  <>
                    关键词“<span className="text-accent-300 font-medium">{keyword}</span>”共找到{" "}
                    <span className="text-accent-300 font-medium">{results.length}</span> 个结果
                  </>
                ) : (
                  <>
                    未输入关键词，正在展示全部 <span className="text-accent-300 font-medium">{results.length}</span> 部作品
                  </>
                )}
              </p>
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 ring-1 ring-background-200/70">
                {typeTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => updateParams({ type: tab.key })}
                    className={`px-4 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                      typeParam === tab.key
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-400 hover:text-accent-300"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {results.length === 0 ? (
              <div className="rounded-lg bg-background-100 ring-1 ring-background-200/70 py-16 px-6 text-center">
                <span className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-400">
                  <i className="ri-search-2-line" aria-hidden="true" />
                </span>
                <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-900">没有找到相关作品</h2>
                <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或者看看下面的热门推荐。</p>
                <div className="mt-8 text-left">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">热门影视推荐</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
                    {hot.map((item) => (
                      <PosterCard key={item.id} item={item} />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
                {results.map((item) => (
                  <PosterCard key={item.id} item={item} />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}